/**
 * Writes one tile's block into the planet's buffers: the positions and normals of its top, its
 * rounded edge and its wall, and their colours. The stylized look also shades the middle under
 * what stands there, blends ground corners into a neighbour of another kind, and fills `aTile`
 * (seed, kind, how far from the middle, shore) and `aColumn` (the layers under the tile, for the
 * bands on its wall) for its surface shader. A tile the view doesn't show collapses to a point.
 */
import { KIND } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { WATER_DEEP, WATER_DEEPER } from '@core/palette';

import type { Look } from '../style/look';
import { tileBase } from './colors';
import { packColumn, type Column } from './column';
import { PROFILE, RB, SEG, type Layout } from './layout';
import { blendsAtCorners, cornerOthers, isShown, shadeUnder, shoreAt, tileSeed } from './surface';
import { radiusOf, STEP, type PlanetView } from './view';

/**
 * The wall under a tile's rim: deep enough that a pit next to a raised tile shows no gap, and on a
 * planet with layers down to a level under the core, so the deepest pit shows no gap either.
 */
const SKIRT = 1.2;
const skirtOf = (view: PlanetView): number =>
  view.layers ? Math.max(SKIRT, (view.layers.coreDepth + 1) * STEP) : SKIRT;
/** How far a ground corner leans towards two neighbours of another kind (half for one). */
const BLEND = 0.35;

export interface TileBuffers {
  position: Float32Array;
  normal: Float32Array;
  color: Float32Array;
  /** Per vertex: seed, kind, 0 in the middle, 1 at the top's rim and 2 down the edge, shore. Null for the classic look. */
  tile: Float32Array | null;
  /** Per vertex: 1 where an extractor draws from the tile (it glows). Null for the classic look. */
  glow: Float32Array | null;
  /** Per vertex: the tile's column of layers, packed (column.ts). Null for the classic look. */
  column: Float32Array | null;
}

export interface TilePaint {
  readonly look: Look;
  /** The tile I stand on: a lighter top and a rim in `tint`. */
  here: number;
  readonly tint: THREE.Color;
  /** Where a tile's top stands above the planet's radius: its level, its kind's height, a bounce. */
  top(tile: number, kind: number): number;
}

const _b = new THREE.Color();
const _col = new THREE.Color();
const _cc = new THREE.Color();
const _corner = new THREE.Color();
const _sum = new THREE.Color();
const _nb = new THREE.Color();
const _white = new THREE.Color(0xffffff);
const _deep = new THREE.Color(WATER_DEEP);
const _deeper = new THREE.Color(WATER_DEEPER);
const others: [number, number] = [-1, -1];
/** `aTile`'s kind for rock a charge broke: this far past its kind, so the shader cracks its top. */
export const BROKEN_MARK = 32;
const _column: Column = [0, 0, 0, 0];

function put(
  buf: TileBuffers,
  v: number,
  x: number,
  y: number,
  z: number,
  nx: number,
  ny: number,
  nz: number,
  col: THREE.Color,
): void {
  const p = buf.position;
  const n = buf.normal;
  const c = buf.color;
  p[v * 3] = x;
  p[v * 3 + 1] = y;
  p[v * 3 + 2] = z;
  n[v * 3] = nx;
  n[v * 3 + 1] = ny;
  n[v * 3 + 2] = nz;
  c[v * 3] = col.r;
  c[v * 3 + 1] = col.g;
  c[v * 3 + 2] = col.b;
}

function data(
  buf: TileBuffers,
  v: number,
  seed: number,
  kind: number,
  edge: number,
  shore: number,
) {
  const t = buf.tile;
  if (!t) return;
  t[v * 4] = seed;
  t[v * 4 + 1] = kind;
  t[v * 4 + 2] = edge;
  t[v * 4 + 3] = shore;
}

/** Adds neighbour `j`'s colour to `_sum` when it is drawn ground of another kind; 1 if it counted. */
function blendWith(view: PlanetView, j: number, kind: number): number {
  if (!isShown(view.shown, j)) return 0;
  const other = view.kind[j] ?? 0;
  if (other === kind || !blendsAtCorners(other)) return 0;
  _sum.add(tileBase(view, j, _nb));
  return 1;
}

/** A ground corner's colour: the tile's own, leaning towards ground of another kind that meets it there. */
function cornerColor(view: PlanetView, i: number, k: number, kind: number, base: THREE.Color) {
  _corner.copy(base);
  if (!blendsAtCorners(kind)) return _corner;
  cornerOthers(view.grid, i, k, others);
  _sum.setRGB(0, 0, 0);
  const n = blendWith(view, others[0], kind) + blendWith(view, others[1], kind);
  if (n > 0) _corner.lerp(_sum.multiplyScalar(1 / n), (BLEND * n) / 2);
  return _corner;
}

/** The same column on every vertex of a tile: its wall reads the bands from it. */
function fillColumn(buf: TileBuffers, from: number, to: number, column: Column): void {
  const c = buf.column;
  if (!c) return;
  for (let v = from; v < to; v++) {
    c[v * 4] = column[0];
    c[v * 4 + 1] = column[1];
    c[v * 4 + 2] = column[2];
    c[v * 4 + 3] = column[3];
  }
}

/** Every vertex of a hidden tile at the planet's centre: its triangles have no area. */
function collapse(buf: TileBuffers, from: number, to: number): void {
  _cc.setRGB(0, 0, 0);
  for (let v = from; v < to; v++) {
    put(buf, v, 0, 0, 0, 0, 1, 0, _cc);
    data(buf, v, 0, 0, 0, 0);
  }
  buf.glow?.fill(0, from, to);
  buf.column?.fill(0, from * 4, to * 4);
}

/** Writes tile `i` into `buf` from vertex `o` on (where its chunk keeps it). */
export function writeTile(
  view: PlanetView,
  layout: Layout,
  buf: TileBuffers,
  paint: TilePaint,
  i: number,
  o: number,
): void {
  const { grid } = view;
  if (!isShown(view.shown, i)) {
    collapse(buf, o, o + (layout.off[i + 1] ?? 0) - (layout.off[i] ?? 0));
    return;
  }
  const stylized = paint.look === 'stylized';
  const kind = view.kind[i] ?? 0;
  const rt = radiusOf(grid.subdivisions) + paint.top(i, kind);
  const rb = radiusOf(grid.subdivisions) - skirtOf(view);
  const cx = grid.centers[i * 3] ?? 0;
  const cy = grid.centers[i * 3 + 1] ?? 0;
  const cz = grid.centers[i * 3 + 2] ?? 0;
  const nc = (grid.corners[i]?.length ?? 0) / 3;
  const co = layout.coff[i] ?? 0;
  const b = tileBase(view, i, _b);
  const hl = i === paint.here;
  const seed = tileSeed(i);
  const mark = view.shattered?.[i] ? kind + BROKEN_MARK : kind;
  const count = (layout.off[i + 1] ?? 0) - (layout.off[i] ?? 0);
  // the wall goes down from the rounded edge's foot to the skirt: that many height levels of layers
  const bands = (rt - RB - rb) / STEP;
  fillColumn(buf, o, o + count, packColumn(view, i, _column));
  _col.copy(b);
  // the classic look darkens a pond's middle; the stylized surface shades water by its shore
  if (kind === KIND.WATER && !stylized) _col.lerp(view.groups[i] === 1 ? _deeper : _deep, 0.5);
  if (stylized) _col.multiplyScalar(shadeUnder(kind));
  if (hl) _col.lerp(_white, 0.1);
  put(buf, o, cx * rt, cy * rt, cz * rt, cx, cy, cz, _col);
  data(buf, o, seed, mark, 0, 0);
  buf.glow?.fill(view.drawn?.[i] ?? 0, o, o + count);
  for (let k = 0; k < nc; k++) {
    const j = (co + k) * 3;
    const qx = layout.cor[j] ?? 0;
    const qy = layout.cor[j + 1] ?? 0;
    const qz = layout.cor[j + 2] ?? 0;
    const ox = layout.out[j] ?? 0;
    const oy = layout.out[j + 1] ?? 0;
    const oz = layout.out[j + 2] ?? 0;
    const d = rt / (qx * cx + qy * cy + qz * cz);
    const shore = stylized && kind === KIND.WATER ? shoreAt(grid, view.kind, view.shown, i, k) : 0;
    const corner = stylized ? cornerColor(view, i, k, kind, b) : b;
    // where only water meets, a lake is one surface: the corner lies flat, on the sphere
    const flat = stylized && kind === KIND.WATER && shore === 0;
    for (let r = 0; r <= SEG; r++) {
      const pr = PROFILE[r];
      if (!pr) continue;
      const v = o + 1 + r * nc + k;
      if (flat) {
        put(buf, v, qx * rt, qy * rt, qz * rt, qx, qy, qz, b);
        data(buf, v, seed, mark, 1, shore);
        continue;
      }
      const x = cx * rt + (qx * d - cx * rt) * pr.s + cx * pr.h;
      const y = cy * rt + (qy * d - cy * rt) * pr.s + cy * pr.h;
      const z = cz * rt + (qz * d - cz * rt) * pr.s + cz * pr.h;
      if (stylized) _cc.copy(corner).multiplyScalar(1 - 0.2 * pr.sn);
      else _cc.copy(b).multiplyScalar(1 - 0.1 * pr.sn);
      if (hl) _cc.lerp(paint.tint, 0.35 + 0.55 * pr.sn);
      put(
        buf,
        v,
        x,
        y,
        z,
        cx * pr.cs + ox * pr.sn,
        cy * pr.cs + oy * pr.sn,
        cz * pr.cs + oz * pr.sn,
        _cc,
      );
      // 1 at the top's rim, rising to 2 down the rounded edge
      data(buf, v, seed, mark, 1 + pr.sn, shore);
    }
    _cc.copy(b).multiplyScalar(stylized ? 0.6 : 0.72);
    const w = o + 1 + (SEG + 1) * nc + k;
    put(buf, w, qx * rb, qy * rb, qz * rb, ox, oy, oz, _cc);
    // past 2 down the wall: how many height levels below the top, for its bands
    data(buf, w, seed, mark, 2 + bands, shore);
  }
}
